import { useRef, useState } from 'react'

import { reasonRefusal, reasonRemaining, serialiseReason } from '@/lib/data/reasoncode'
import { formatMinor } from '../format'
import type { FormBlock, FormField, FormPanel, RowColumn } from '../spec/types'

/**
 * The console's general write control: create a record, or publish a version
 * over the one that is live.
 *
 * # What it inherits from `Decide`, deliberately
 *
 * `Decide` was the first write and it settled the posture every other one
 * copies: no field starts at a value nobody chose, the button is dead until the
 * form is answerable and the sentence beside it names what is still missing,
 * the audit line is legible BEFORE the click (law 21), and the answer replaces
 * the controls in place as a panel rather than a toast that expires.
 *
 * The differences are the ones law 6 forces. A decision picks one of a closed
 * set of outcomes; a form carries values, and a versioned entity's form carries
 * them *against what is live now*.
 *
 * # Law 6 is enforced here, not left to the caller
 *
 * When `versioned` is set the component — not the adapter — supplies the
 * `effective_from` field, labels the button "Save as new version", and renders
 * the diff against `baseline`. Leaving any of the three to each call site is
 * how the fifth screen ends up with a "Save" button: twenty adapters cannot be
 * relied on to remember a rule that one component can hold.
 *
 * The diff is drawn from `baseline` matched to fields BY LABEL rather than by
 * position, so a form that grows a field does not silently start comparing the
 * new one against the old one's live value.
 *
 * # Amounts are minor units as text, and never a number
 *
 * Law 9. The input is `inputMode="numeric"` over a string that is submitted
 * unchanged; nothing here parses to a JS number, so a value a double cannot
 * hold exactly is carried exactly. The currency is rendered beside the field
 * and submitted with it — law 8 admits no bare figure, including one an
 * operator is typing.
 *
 * # `confirm` is for irreversible acts, and there are no deletes
 *
 * The admin surface registers no DELETE at all; what stands in its place are
 * POSTs named for what they do — `/retire`, `/close`, `/resolve`, `/lift` — and
 * the row is never removed, it stops governing. Several of those are still
 * one-way, and where an act cannot be walked back the control asks the operator
 * to type the record's own identifier. That is not ceremony: the difference
 * between retiring `eur-out` and `eur-in` is invisible in a dialog that only
 * asks whether you are sure.
 */

interface Props {
  readonly block: FormBlock
  /** Standby cell, or a role this screen is not scoped to — law 18/19. */
  readonly writeBlocked: boolean
  readonly writeNote: string
}

/** The field law 6 adds to every versioned form. Never declared by a caller. */
const EFFECTIVE_FROM: FormField = {
  name: 'effective_from',
  label: 'Effective from',
  kind: 'date',
  required: true,
  note: 'The instant this version starts governing. A version with no start governs nothing.',
}

export function Form({ block, writeBlocked, writeNote }: Props) {
  const fields = block.versioned === true ? [...block.fields, EFFECTIVE_FROM] : block.fields

  /**
   * Field values, seeded from `value` so a versioned edit starts at what is
   * live. A field with no `value` starts empty and stays empty until typed —
   * there is no defaulting anywhere in here.
   */
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, f.value ?? ''])),
  )
  const [code, setCode] = useState('')
  const [note, setNote] = useState('')
  const [typed, setTyped] = useState('')
  const [inFlight, setInFlight] = useState(false)
  const [panel, setPanel] = useState<FormPanel | null>(null)

  /**
   * The double-submit guard, and why it is a ref rather than the state above.
   *
   * `setInFlight(true)` does not take effect until React re-renders, so two
   * clicks dispatched in the same task both pass the `inFlight` check and both
   * reach the backend. The disabled attribute has the same lag. A ref is
   * written synchronously, so the second call returns before it can post.
   *
   * This matters more here than it would on most surfaces: **no admin POST
   * reads an `Idempotency-Key` header.** Publishing a version, assigning a
   * price list and raising a flag are not idempotent, so a double-click is not
   * a wasted request — it is a second version, a second assignment, a second
   * flag, each with its own id and its own audit row, and the operator sees one
   * result panel and believes one thing happened.
   *
   * The guard belongs in this component rather than in each adapter for the
   * same reason law 6's wording does: twelve call sites cannot each be relied
   * on to remember it, and the one that forgets is discovered by an operator
   * with two versions and no idea which is live.
   */
  const posting = useRef(false)

  const unwired = block.submit === undefined
  // Same order as `Decide`: the shell's posture first, because nothing the
  // operator types will move it; then what the endpoint can do; then what they
  // have not filled in yet.
  const shellBlock = writeBlocked
    ? writeNote
    : block.blocked !== ''
      ? block.blocked
      : unwired
        ? 'This console cannot submit this form: no endpoint is wired behind the control.'
        : ''

  const blank = fields.filter((f) => f.required && !answered(f, values[f.name] ?? ''))
  const reasonBad = block.reasons === undefined ? null : reasonRefusal(code, note)
  const confirmBad =
    block.confirm !== undefined && typed !== block.confirm
      ? `Type ${block.confirm} to confirm. This cannot be undone.`
      : ''

  const missing =
    blank.length > 0
      ? `Still required: ${blank.map((f) => f.label).join(', ')}.`
      : (reasonBad ?? confirmBad)

  const stop = shellBlock !== '' ? shellBlock : missing
  const done = panel !== null && panel.final
  const disabled = stop !== '' || inFlight || done

  /** Only the shell's refusals freeze the inputs — see `Decide` for why. */
  const frozen = shellBlock !== '' || inFlight

  function set(name: string, v: string) {
    setValues((prev) => ({ ...prev, [name]: v }))
  }

  async function submit() {
    if (block.submit === undefined || missing !== '' || posting.current) return
    posting.current = true
    setInFlight(true)
    // Never rejects — see `FormSubmit`. Every answer is a panel.
    const answer = await block.submit(
      block.reasons === undefined
        ? values
        : { ...values, reason: serialiseReason(code, note) },
    )
    posting.current = false
    setInFlight(false)
    setPanel(answer)
  }

  /**
   * What changed against what is live — law 6's diff.
   *
   * Matched by LABEL, and a field with no baseline line is reported as such
   * rather than as a change from empty: "this entity had no such field until
   * now" and "this field was blank" are different facts, and only one of them
   * is a change an operator made.
   */
  const diff =
    block.versioned === true && block.baseline !== undefined
      ? fields
          .filter((f) => f.name !== 'effective_from')
          .map((f) => {
            const live = block.baseline?.find(([label]) => label === f.label)?.[1]
            const next = values[f.name] ?? ''
            return { label: f.label, live, next, changed: live !== undefined && live !== next }
          })
      : []
  const changes = diff.filter((d) => d.changed)

  return (
    <div
      style={{
        border: '1px solid var(--k-line)',
        borderTop: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
        maxWidth: '900px',
      }}
    >
      <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
        <div style={{ fontSize: '12.5px', fontWeight: 600 }}>{block.lead}</div>
      </div>

      {panel !== null && <ResultPanel panel={panel} />}

      {!done && (
        <>
          <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 14px' }}>
              {fields.map((f) => (
                <Field
                  key={f.name}
                  field={f}
                  value={values[f.name] ?? ''}
                  // The currency this amount is in, when another field carries
                  // it. Empty means the operator has not chosen one yet, and
                  // the field says that rather than assuming a currency.
                  currency={
                    f.currency
                    ?? (f.currencyFrom === undefined ? undefined : values[f.currencyFrom])
                  }
                  frozen={frozen}
                  onChange={(v) => set(f.name, v)}
                />
              ))}
            </div>
          </div>

          {block.versioned === true && block.baseline !== undefined && (
            <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
              <div style={FIELD_LABEL}>Diff against the version in force</div>
              {changes.length === 0 ? (
                <div style={{ fontSize: '11.5px', color: 'var(--k-mut)' }}>
                  Nothing differs from what is live.
                </div>
              ) : (
                changes.map((d) => (
                  <div
                    key={d.label}
                    style={{ display: 'flex', gap: '10px', marginTop: '5px', flexWrap: 'wrap' }}
                  >
                    <span style={{ fontSize: '11px', color: 'var(--k-mut2)', minWidth: '170px' }}>
                      {d.label}
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        fontFamily: 'var(--k-mono)',
                        textDecoration: 'line-through',
                        color: 'var(--k-mut)',
                      }}
                    >
                      {d.live === '' ? '—' : d.live}
                    </span>
                    <span style={{ fontSize: '12px', fontFamily: 'var(--k-mono)', fontWeight: 700 }}>
                      {d.next === '' ? '—' : d.next}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          {block.reasons !== undefined && (
            <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'flex-end' }}>
                <div>
                  <div style={FIELD_LABEL}>Reason code — required</div>
                  <select
                    aria-label="Reason code"
                    value={code}
                    disabled={frozen}
                    onChange={(e) => setCode(e.target.value)}
                    style={{ ...CONTROL, minWidth: '250px' }}
                  >
                    {/* Unselectable placeholder, never a first real code — a
                        select whose first option is already valid is a default. */}
                    <option value="">— choose a reason code</option>
                    {block.reasons.map(([value, gloss]) => (
                      <option key={value} value={value}>
                        {`${value} — ${gloss}`}
                      </option>
                    ))}
                  </select>
                  {block.reasonNote !== undefined && (
                    <div style={FIELD_NOTE}>{block.reasonNote}</div>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={FIELD_LABEL}>Note — optional, goes in the same field</div>
                  <input
                    aria-label="Reason note"
                    value={note}
                    disabled={frozen}
                    onChange={(e) => setNote(e.target.value)}
                    style={{ ...CONTROL, width: '100%' }}
                  />
                  <div style={{ ...FIELD_NOTE, fontFamily: 'var(--k-mono)' }}>
                    {/* Bytes, because the server's cap is Go's len(). */}
                    {`${String(reasonRemaining(code, note))} of 200 bytes left`}
                  </div>
                </div>
              </div>
            </div>
          )}

          {block.confirm !== undefined && (
            <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
              <div style={FIELD_LABEL}>{`Type ${block.confirm} to confirm`}</div>
              <input
                aria-label="Confirmation"
                value={typed}
                disabled={frozen}
                onChange={(e) => setTyped(e.target.value)}
                style={{ ...CONTROL, minWidth: '250px', fontFamily: 'var(--k-mono)' }}
              />
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
              padding: '11px 12px',
              background: 'var(--k-bg)',
            }}
          >
            <button
              type="button"
              disabled={disabled}
              data-dim={disabled ? '1' : ''}
              onClick={() => void submit()}
              style={{
                height: '29px',
                padding: '0 13px',
                border: 0,
                background: 'var(--k-accent)',
                color: 'var(--k-accentink)',
                font: 'inherit',
                fontWeight: 800,
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {/* Law 6: a versioned entity is never saved, it gains a version.
                  The word is the component's and not the caller's. */}
              {inFlight
                ? 'Submitting…'
                : block.versioned === true
                  ? 'Save as new version'
                  : block.cta}
            </button>
            <span style={{ fontSize: '11.5px', color: 'var(--k-mut)', maxWidth: '60ch' }}>
              {stop === '' ? block.ctaNote : stop}
            </span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '11px',
                color: 'var(--k-mut2)',
                fontFamily: 'var(--k-mono)',
              }}
            >
              {block.auditLine}
            </span>
          </div>
        </>
      )}

      <div style={{ padding: '0 12px 11px', fontSize: '11px', color: 'var(--k-mut2)' }}>
        {writeNote}
      </div>
    </div>
  )
}

/**
 * Whether a required field has been answered.
 *
 * A `pairs` field carries its rows as JSON in the same string map, so that the
 * rest of the component — required-checking, the diff, submit — needs no
 * special case for it. It is answered when at least one row has both halves;
 * a row with a key and no body is a sentence nobody wrote.
 */
function answered(field: FormField, raw: string): boolean {
  if (field.kind === 'pairs') {
    return readPairs(raw).some((r) => r.k.trim() !== '' && r.v.trim() !== '')
  }
  if (field.kind === 'rows') {
    // A `rows` field is answered when at least one row is COMPLETE. A row with
    // some cells filled is a line somebody started and did not finish, and
    // treating it as an answer would publish a priced line with no price.
    const cols = field.columns ?? []
    return readRows(raw).some((r) => rowIsComplete(r, cols))
  }
  return raw.trim() !== ''
}

/** Every required cell filled. An entirely blank row is not complete. */
function rowIsComplete(row: Readonly<Record<string, string>>, cols: readonly RowColumn[]): boolean {
  const touched = cols.some((c) => (row[c.name] ?? '').trim() !== '')
  if (!touched) return false
  return cols.every((c) => !c.required || (row[c.name] ?? '').trim() !== '')
}

function readRows(raw: string): readonly Record<string, string>[] {
  if (raw.trim() === '') return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.map((row) => {
      const out: Record<string, string> = {}
      if (typeof row === 'object' && row !== null) {
        for (const [k, v] of Object.entries(row as Record<string, unknown>)) {
          if (typeof v === 'string') out[k] = v
        }
      }
      return out
    })
  } catch {
    return []
  }
}

interface Pair { readonly k: string; readonly v: string }

function readPairs(raw: string): readonly Pair[] {
  if (raw.trim() === '') return [{ k: '', v: '' }]
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return [{ k: '', v: '' }]
    return parsed.map((row) => {
      const r = row as Record<string, unknown>
      return { k: typeof r['k'] === 'string' ? r['k'] : '', v: typeof r['v'] === 'string' ? r['v'] : '' }
    })
  } catch {
    return [{ k: '', v: '' }]
  }
}

function Field({
  field,
  value,
  currency,
  frozen,
  onChange,
}: {
  readonly field: FormField
  readonly value: string
  /** Resolved by the caller: the field's own, or the one another field holds. */
  readonly currency: string | undefined
  readonly frozen: boolean
  readonly onChange: (v: string) => void
}) {
  const label = `${field.label}${field.required ? ' — required' : ''}`
  const mono = field.mono === true || field.kind === 'amount'

  return (
    <div
      style={{
        flex: field.kind === 'textarea' || field.kind === 'rows' ? '1 1 100%' : '1 1 250px',
        minWidth: '210px',
      }}
    >
      <div style={FIELD_LABEL}>{label}</div>

      {field.kind === 'rows' ? (
        <Rows field={field} value={value} frozen={frozen} onChange={onChange} />
      ) : field.kind === 'pairs' ? (
        <Pairs field={field} value={value} frozen={frozen} onChange={onChange} />
      ) : field.kind === 'select' ? (
        <select
          aria-label={field.label}
          value={value}
          disabled={frozen}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...CONTROL, width: '100%' }}
        >
          <option value="">— choose</option>
          {(field.options ?? []).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      ) : field.kind === 'textarea' ? (
        <textarea
          aria-label={field.label}
          value={value}
          disabled={frozen}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          style={{ ...CONTROL, width: '100%', height: 'auto', padding: '6px 8px' }}
        />
      ) : field.kind === 'checkbox' ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: '7px', height: '28px' }}>
          <input
            type="checkbox"
            aria-label={field.label}
            checked={value === 'true'}
            disabled={frozen}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          />
          <span style={{ fontSize: '12px' }}>{value === 'true' ? 'yes' : 'no'}</span>
        </label>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            aria-label={field.label}
            type={field.kind === 'date' ? 'date' : 'text'}
            // Minor units, typed as digits and submitted as the string typed —
            // law 9. Never `type="number"`, which would hand the value to the
            // browser's float parser on the way out.
            {...(field.kind === 'amount' ? { inputMode: 'numeric' as const } : {})}
            value={value}
            disabled={frozen}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
            style={{
              ...CONTROL,
              width: '100%',
              ...(mono ? { fontFamily: 'var(--k-mono)' } : {}),
            }}
          />
          {field.kind === 'amount' && (
            /* Law 8 — the currency is on the field, not implied by the locale.
               An unchosen currency says so; it never falls back to one. */
            <span style={{ fontSize: '11px', color: 'var(--k-mut2)', fontFamily: 'var(--k-mono)' }}>
              {currency === undefined || currency === '' ? '— currency not chosen' : currency}
            </span>
          )}
        </div>
      )}

      {field.kind === 'amount' && currency !== undefined && currency !== '' && value.trim() !== '' && (
        <div style={{ ...FIELD_NOTE, fontFamily: 'var(--k-mono)' }}>
          {/* What the minor units they typed actually mean, at that currency's
              own exponent. "Never assume two decimal places" is law 9, and this
              is where an operator finds out JPY has none. */}
          {readBack(value, currency)}
        </div>
      )}

      {field.note !== undefined && field.kind !== 'amount' && (
        <div style={FIELD_NOTE}>{field.note}</div>
      )}
    </div>
  )
}

/**
 * The typed minor units read back at the currency's scale.
 *
 * Non-digits produce a statement that it is not a number rather than a
 * formatted zero: a zero here would tell an operator their typo was accepted.
 */
function readBack(minor: string, currency: string): string {
  if (!/^-?\d+$/.test(minor.trim())) return `not minor units — digits only, in ${currency}`
  return `${formatMinor(BigInt(minor.trim()), currency)} ${currency}`
}

/**
 * A repeating editor over declared columns.
 *
 * # Where the money laws are enforced for everything that comes after this
 *
 * Law 8: an `amount` cell renders its currency beside it, read from the column
 * named by `currencyFrom` IN THE SAME ROW. Until that row's currency is chosen
 * the cell says so rather than assuming one — a priced line with an implied
 * currency is the failure law 8 exists to prevent, and a table of them is the
 * same failure repeated per line.
 *
 * Law 9: minor units, typed as digits, submitted as the string typed, read back
 * at the currency's OWN exponent from the ISO-4217 table. `readBack` is shared
 * with the single-amount field, so a table cell and a lone field cannot
 * disagree about what 1000 JPY means.
 *
 * # A blank cell is not a zero
 *
 * `nullWhenBlank` columns emit their cell as the empty string, and the adapter
 * turns that into `null`. This component never substitutes a zero and never
 * drops a cell: a floor of zero and no floor are different requests, and the
 * distinction is only preserved if every layer refuses to invent one.
 *
 * # Incomplete rows are dropped, not refused
 *
 * The trailing row is always blank so there is somewhere to type. A row whose
 * required cells are not all filled is dropped at submit rather than blocking
 * it, matching `Pairs` — with one difference that matters: `answered` requires
 * at least one COMPLETE row, so a form whose only content is a half-typed line
 * will not submit an empty set and report success.
 */
function Rows({
  field,
  value,
  frozen,
  onChange,
}: {
  readonly field: FormField
  readonly value: string
  readonly frozen: boolean
  readonly onChange: (v: string) => void
}) {
  const cols = field.columns ?? []
  const rows = [...readRows(value)]
  const last = rows[rows.length - 1]
  if (last === undefined || cols.some((c) => (last[c.name] ?? '').trim() !== '')) {
    rows.push({})
  }

  function set(i: number, name: string, next: string) {
    onChange(JSON.stringify(rows.map((r, j) => (j === i ? { ...r, [name]: next } : r))))
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '4px', minWidth: 'min-content' }}>
        {cols.map((c) => (
          <div key={c.name} style={{ ...FIELD_LABEL, flex: c.width ?? '1 1 0', marginTop: 0 }}>
            {c.label}
            {c.required ? ' *' : ''}
          </div>
        ))}
      </div>

      {rows.map((row, i) => (
        <div
          // Positional, like `Pairs`: reordering is not offered, so the index is
          // stable for as long as the row exists.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{ display: 'flex', gap: '8px', marginBottom: '6px', minWidth: 'min-content' }}
        >
          {cols.map((c) => (
            <div key={c.name} style={{ flex: c.width ?? '1 1 0' }}>
              <RowCell
                col={c}
                row={row}
                index={i}
                frozen={frozen}
                onChange={(next) => set(i, c.name, next)}
              />
            </div>
          ))}
        </div>
      ))}

      <div style={FIELD_NOTE}>
        Published together as one version. A row whose required cells are not all
        filled is dropped. An empty optional cell means <b>absent</b>, not zero.
      </div>
    </div>
  )
}

function RowCell({
  col,
  row,
  index,
  frozen,
  onChange,
}: {
  readonly col: RowColumn
  readonly row: Readonly<Record<string, string>>
  readonly index: number
  readonly frozen: boolean
  readonly onChange: (v: string) => void
}) {
  const value = row[col.name] ?? ''
  const label = `${col.label} ${String(index + 1)}`
  // Law 8, per row. The currency is whatever THIS line chose; a sibling line
  // pricing in another currency must not change what this one reads as.
  const currency = col.currencyFrom === undefined ? undefined : row[col.currencyFrom]

  if (col.kind === 'select') {
    return (
      <select
        aria-label={label}
        value={value}
        disabled={frozen}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...CONTROL, width: '100%' }}
      >
        <option value="">— choose</option>
        {(col.options ?? []).map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    )
  }

  return (
    <div>
      <input
        aria-label={label}
        type="text"
        {...(col.kind === 'amount' ? { inputMode: 'numeric' as const } : {})}
        value={value}
        disabled={frozen}
        placeholder={col.placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...CONTROL,
          width: '100%',
          ...(col.kind === 'amount' ? { fontFamily: 'var(--k-mono)' } : {}),
        }}
      />
      {col.kind === 'amount' && (
        <div style={{ ...FIELD_NOTE, fontFamily: 'var(--k-mono)', marginTop: '2px' }}>
          {currency === undefined || currency === ''
            ? '— currency not chosen on this line'
            : value.trim() === ''
              ? col.nullWhenBlank === true
                ? `no ${col.label.toLowerCase()} — absent, not zero`
                : currency
              : readBack(value, currency)}
        </div>
      )}
    </div>
  )
}

/**
 * A repeating two-column editor.
 *
 * # Why it is not a textarea of JSON
 *
 * It would have been three lines. But the operator using this is an editor
 * writing customer-facing sentences, and asking them to balance braces to fix a
 * typo puts a syntax error between a support ticket and the words a customer
 * reads. The rows are the shape the backend takes, so the control is that shape.
 *
 * # Empty rows are dropped rather than refused
 *
 * The last row is always blank so there is somewhere to type; submitting sends
 * only the rows with both halves. A row with a key and no body is dropped too —
 * it is a sentence nobody wrote, and sending it would publish an empty string
 * over whatever that key currently says.
 */
function Pairs({
  field,
  value,
  frozen,
  onChange,
}: {
  readonly field: FormField
  readonly value: string
  readonly frozen: boolean
  readonly onChange: (v: string) => void
}) {
  const rows = [...readPairs(value)]
  if (rows.length === 0 || (rows[rows.length - 1]?.k !== '' || rows[rows.length - 1]?.v !== '')) {
    rows.push({ k: '', v: '' })
  }
  const labels = field.pair ?? { keyLabel: 'Key', keyName: 'key', valueLabel: 'Value', valueName: 'value' }

  function set(i: number, part: 'k' | 'v', next: string) {
    const copy = rows.map((r, j) => (j === i ? { ...r, [part]: next } : r))
    onChange(JSON.stringify(copy))
  }

  return (
    <div>
      {rows.map((row, i) => (
        <div
          // Rows are positional and have no id; reordering is not offered, so
          // the index is stable for as long as the row exists.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}
        >
          <input
            aria-label={`${labels.keyLabel} ${String(i + 1)}`}
            value={row.k}
            disabled={frozen}
            placeholder={labels.keyLabel}
            onChange={(e) => set(i, 'k', e.target.value)}
            style={{ ...CONTROL, flex: '0 0 34%', fontFamily: 'var(--k-mono)' }}
          />
          <input
            aria-label={`${labels.valueLabel} ${String(i + 1)}`}
            value={row.v}
            disabled={frozen}
            placeholder={labels.valueLabel}
            onChange={(e) => set(i, 'v', e.target.value)}
            style={{ ...CONTROL, flex: 1 }}
          />
        </div>
      ))}
      <div style={FIELD_NOTE}>
        {/* The set is the unit of work: these publish as ONE revision, so a
            sentence and the sentence explaining it cannot end up citing
            different revisions. */}
        Published together as one revision. Rows with an empty half are dropped.
      </div>
    </div>
  )
}

function ResultPanel({ panel }: { readonly panel: FormPanel }) {
  return (
    <div
      data-t={panel.tone}
      style={{
        borderBottom: '1px solid var(--k-line)',
        borderLeft: `3px solid var(--k-${panel.tone})`,
        background: 'var(--k-panel2)',
        padding: '11px 12px',
      }}
    >
      <div style={{ fontSize: '12.5px', fontWeight: 700 }}>{panel.heading}</div>
      {panel.lines.map(([label, value, hint]) => (
        <div key={label} style={{ display: 'flex', gap: '10px', marginTop: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: 'var(--k-mut2)', minWidth: '170px' }}>
            {label}
          </span>
          <span style={{ fontSize: '12px', fontFamily: 'var(--k-mono)', fontWeight: 600 }}>
            {value}
          </span>
          {hint !== '' && <span style={{ fontSize: '11px', color: 'var(--k-mut)' }}>{hint}</span>}
        </div>
      ))}
      {panel.note !== '' && (
        <div
          style={{
            fontSize: '11.5px',
            color: 'var(--k-mut)',
            marginTop: '8px',
            maxWidth: '78ch',
            textWrap: 'pretty',
          }}
        >
          {panel.note}
        </div>
      )}
    </div>
  )
}

const CONTROL = {
  height: '28px',
  border: '1px solid var(--k-line)',
  background: 'var(--k-bg)',
  color: 'var(--k-ink)',
  font: 'inherit',
  fontSize: '12px',
  padding: '0 6px',
  borderRadius: 0,
} as const

const FIELD_LABEL = {
  fontSize: '10px',
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'var(--k-mut2)',
  marginBottom: '4px',
} as const

const FIELD_NOTE = {
  fontSize: '11px',
  color: 'var(--k-mut)',
  marginTop: '4px',
} as const
